import React from 'react';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import Dialog from '@material-ui/core/Dialog';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import DialogContent from '@material-ui/core/DialogContent';
import { DialogActions } from '@material-ui/core';
import { IPhenologySpecific } from '../../../../types/model/phenology/specific';
import { IDivision } from '../../../../types/model/rights/division';
import { ICultivar } from '../../../../types/model/masterData/cultivars';
import PhenologySpecificFunctions from '../../../../store/phenology/specific/functions';
import { IAssignmentBlock } from '../../../../types/model/masterData/block';
import DivisionAutocomplete from '../../../customComponents/autocomplete/rights/DivisionAutocomplete';
import CultivarAutocomplete from '../../../customComponents/autocomplete/masterData/CultivarAutocomplete';
import ScoutingBlockLandNameDropdown from '../../../customComponents/dropdowns/BlockLandNameDropdown';
import ScoutingBlockNameDropdown from '../../../customComponents/dropdowns/BlockNameDropdown';
import StadiumButton from '../../../customComponents/button/StadiumButton';

interface IEditPhenologySpecificInfoProps {
    value : IPhenologySpecific;
    fullWidth ?: boolean;
    maxWidth ?: 'xs' | 'sm' | 'md' | 'lg' | false;
    fullScreen ?: boolean;
    transition ?: React.ForwardRefExoticComponent<TransitionProps & React.RefAttributes<unknown>>;

    isLoading ?: boolean;
}

interface IEditPhenologySpecificInfoState {
    open : boolean;

    divisionCode : string | null;
    divisionName : string | null;
    cultivar : string | null;
    cultivarName : string | null;

    block : string | null;
    blockName : string | null;
    landName : string | null;

    selectedBock : IAssignmentBlock | null;
}

export default class EditPhenologySpecificInfo extends React.Component<IEditPhenologySpecificInfoProps, IEditPhenologySpecificInfoState> {
    private readonly initState : IEditPhenologySpecificInfoState = {
        open: false,
        divisionCode: null,
        divisionName: null,
        cultivar: null,
        cultivarName: null,
        block: null,
        blockName: null,
        landName: null,
        selectedBock: null,
    };

    constructor(props : IEditPhenologySpecificInfoProps) {
        super(props);
        this.state = {
            ...this.initState,
        };
    }

    public readonly componentDidUpdate = (prevProps : IEditPhenologySpecificInfoProps, prevState : IEditPhenologySpecificInfoState) => {
        if (!prevState.open && this.state.open) {
            const {
                division,
                landName,
                block,
                blockName,
                divisionName,
                cultivar,
                cultivarName,
                scoutingBlock,
            } = this.props.value;

            this.setState({
                block,
                blockName,
                divisionName,
                divisionCode: division,
                landName,
                cultivar,
                cultivarName,
                selectedBock: scoutingBlock,
            });
        }
    };


    private readonly onDivisionChange = (division ?: IDivision | null) => {
        this.setState({
            divisionCode: division?.id ?? null,
            divisionName: division?.name ?? null,
            landName: null,
            block: null,
            blockName: null,
            selectedBock: null,
        });
    };
    
    private readonly onCultivarChange = (values : Array<ICultivar>) => {
        this.setState({
            cultivar: values[0]?.id ?? null,
            cultivarName: values[0]?.name ?? null,
        });
    };

    private readonly onLandNameChange = (landName ?: string | null) => {
        this.setState({
            landName: landName ?? null,
            block: null,
            blockName: null,
            selectedBock: null,
        });
    };

    private readonly onBlockChange = (blocks : Array<IAssignmentBlock>) => {
        const block = blocks[0] as IAssignmentBlock | null;
        this.setState({
            block: block?.guid ?? null,
            blockName: block?.name ?? null,
            landName: block?.landName ?? null,
            selectedBock: block,
        });
    };

    private readonly save = async () => {
        const { value } = this.props;
        
        try {
            const {
                divisionCode,
                divisionName,
                cultivar,
                cultivarName,
                landName,
                block,
                blockName,
                selectedBock,
            } = this.state;

            if (!divisionCode
            || !divisionName
            || !cultivar
            || !cultivarName
            || !landName
            || !block
            || !blockName
            || !selectedBock) return;

            await PhenologySpecificFunctions.save({
                ...value,
                division: divisionCode.toLocaleLowerCase(),
                divisionName: divisionName,
                cultivar,
                cultivarName,
                landName,
                block,
                blockName,
                scoutingBlock: selectedBock,
            });
            this.onClose();
        } catch (ex) {
            // Ignore
        }

    };

    private readonly onSaveClick = () => {
        this.save();
    };

    private readonly onEditClick = () => {
        this.setState({
            open: true,
        });
    };

    private readonly onClose = () => {
        if (this.props.isLoading) return;

        this.setState({
            open: false,
        });
    };

    public readonly render = () => {
        const {
            transition,
            maxWidth,
            fullWidth,
            fullScreen,
            isLoading,
            value,
        } = this.props;

        const {
            open,
            divisionCode,
            cultivar,
            landName,
            blockName,
        } = this.state;

        return (
            <React.Fragment>
                <Tooltip title='Edit'>
                    <div>
                        <IconButton color='inherit' size='small' onClick={this.onEditClick} aria-label='Edit' disabled={isLoading}>
                            <Icon>edit</Icon>
                        </IconButton>
                    </div>
                </Tooltip>
                <Dialog
                    open={open}
                    TransitionComponent={transition}
                    transitionDuration={500}
                    onClose={this.onClose}
                    maxWidth={maxWidth}
                    fullScreen={fullScreen}
                    fullWidth={fullWidth}
                    aria-labelledby='trap-info-title'
                    aria-describedby='trap-info-description'>
                    <AppBar className='fdr posr aic jcc' position='static' elevation={0}>
                        <Toolbar className={'fdr flx1 aic jcc'}>
                            <Typography variant='h5' color='inherit'>
                                Edit PhenologySpecific  Info
                            </Typography>
                            <span className='flx1' />
                            <Tooltip title='Close'>
                                <div>
                                    <IconButton color='inherit' disabled={isLoading} onClick={this.onClose} aria-label='Add'>
                                        <Icon>close</Icon>
                                    </IconButton>
                                </div>
                            </Tooltip>
                        </Toolbar>
                    </AppBar>
                    <DialogContent className='fdc flx1 hfill p0'>
                        <div className='fdr bcp pt15 pb15 pl25'>
                            <Typography
                                className='fwn cw fs15'
                            >
                                DIVISION
                            </Typography>
                        </div>
                        <div className='fdc p10'>
                            <div className={'fdc flx1 aic'}>
                                <DivisionAutocomplete
                                    required
                                    fullWidth
                                    value={divisionCode}
                                    onChange={this.onDivisionChange}
                                    crop={value.crop}
                                    label={'Select Division'}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <div className='fdr bcp pt15 pb15 pl25'>
                            <Typography
                                className='fwn cw fs15'
                            >
                                KULTIVARS
                            </Typography>
                        </div>
                        <div className='fdc p10'>
                            <div className={'fdc flx1 aic'}>
                                <CultivarAutocomplete
                                    required
                                    fullWidth
                                    value={!cultivar ? [] : [cultivar]}
                                    onChange={this.onCultivarChange}
                                    crop={value.crop}
                                    label='Select Cultivar'
                                    single
                                />
                            </div>
                        </div>
                        <div className='fdr bcp pt15 pb15 pl25'>
                            <Typography
                                className='fwn cw fs15'
                            >
                                FIELD
                            </Typography>
                        </div>
                        <div className='fdc p10'>
                            <div className={'fdc flx1 aic'}>
                                <ScoutingBlockLandNameDropdown
                                    type='scouting'
                                    value={landName}
                                    division={divisionCode}
                                    disabled={!divisionCode}
                                    crop={value.crop}
                                    required
                                    fullWidth
                                    label='Select Field'
                                    onChange={this.onLandNameChange}
                                />
                            </div>
                        </div>
                        <div className='fdr bcp pt15 pb15 pl25'>
                            <Typography
                                className='fwn cw fs15'
                            >
                                BLOCK
                            </Typography>
                        </div>
                        <div className='fdc p10'>
                            <div className={'fdc flx1 aic'}>
                                <ScoutingBlockNameDropdown
                                    value={!blockName ? [] : [blockName]}
                                    landName={landName}
                                    division={divisionCode}
                                    fullWidth
                                    required
                                    single
                                    onChange={this.onBlockChange}
                                    type='scouting'
                                    disabled={!divisionCode}
                                    crop={value.crop}
                                    label='Select Field'
                                />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <div className='fdr'>
                            <StadiumButton className='fw500 fs14 cpd mr15 bw1' variant='text' onClick={this.onClose}>
                                    BACK
                            </StadiumButton>
                            <StadiumButton className='fw500 fs14 cw bcp' onClick={this.onSaveClick}>
                                    SAVE
                            </StadiumButton>
                        </div>
                    </DialogActions>
                </Dialog>
            </React.Fragment>);
    };
}
